/**
 * Reads. Signed in, the browser calls the Flow API directly with the key the user granted (CORS is
 * open); every request asks the SDK for a current key rather than caching one, so the 7-day key
 * rotation stays invisible. Signed out, the same paths go to the public feed at /api/public — a
 * Cloudflare Pages Function holding a server-side key (functions/api/public/[[path]].ts) — so the
 * board works before anyone signs in.
 */
import { getApiKey, signOut } from "./omauth";

const ROOT = (import.meta.env.VITE_OPENMARKETS_BASE || "https://api.openmarkets.ai").replace(/\/+$/, "");
const FLOW_BASE = ROOT.endsWith("/flow/v1") ? ROOT : `${ROOT}/flow/v1`;
const PUBLIC_BASE = "/api/public";
export const STREAM_URL =
  import.meta.env.VITE_OPENMARKETS_STREAM || "wss://api.openmarkets.ai/flow/v1/stream";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** The signed-out feed isn't set up here (no key configured, or no function in front of the app). */
export function isPublicUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.code === "public_unavailable";
}

export interface Envelope<T> {
  data: T;
  pagination?: { next_cursor: string | null; has_more: boolean };
}

interface ErrorBody {
  error?: { code?: string; message?: string; details?: { retry_after_seconds?: number } };
}

type Query = Record<string, string | number | undefined>;

// The board fans out one liquidity request per game (there is no bulk endpoint — FRICTION.md),
// so cap concurrency instead of firing thirty requests at once into a per-minute rate limit.
const MAX_IN_FLIGHT = 6;
let inFlight = 0;
const waiting: Array<() => void> = [];

async function acquire(): Promise<void> {
  if (inFlight < MAX_IN_FLIGHT) {
    inFlight++;
    return;
  }
  // The releasing request hands its slot straight over, so the count doesn't change.
  await new Promise<void>((resolve) => waiting.push(resolve));
}

function release(): void {
  const next = waiting.shift();
  if (next) next();
  else inFlight--;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(signal.reason);
      },
      { once: true },
    );
  });
}

export async function apiGet<T>(path: string, query: Query = {}, signal?: AbortSignal): Promise<Envelope<T>> {
  await acquire();
  try {
    for (let attempt = 0; ; attempt++) {
      const key = await getApiKey();
      const url = key ? new URL(FLOW_BASE + path) : new URL(PUBLIC_BASE + path, window.location.origin);
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
      }
      const headers: Record<string, string> = { Accept: "application/json" };
      if (key) headers["X-API-Key"] = key;

      const res = await fetch(url, { headers, signal });
      // A static host with no function in front answers /api/public with the SPA's index.html.
      if (!key && !(res.headers.get("content-type") ?? "").includes("json")) {
        throw new ApiError(503, "public_unavailable", "The public odds feed isn't available here.");
      }
      const body: unknown = await res.json().catch(() => null);

      if (res.ok) {
        return body && typeof body === "object" && "data" in body
          ? (body as Envelope<T>)
          : { data: body as T };
      }

      const err = (body as ErrorBody | null)?.error;
      // The X-RateLimit-* headers aren't CORS-exposed, so the retry hint comes from the body.
      if (res.status === 429 && attempt < 2) {
        await sleep((err?.details?.retry_after_seconds ?? 2) * 1000 * (attempt + 1), signal);
        continue;
      }
      // Revoked or uninstalled from the OpenMarkets dashboard → back to the signed-out board.
      if (res.status === 401 && key) signOut();
      throw new ApiError(
        res.status,
        err?.code ?? `http_${res.status}`,
        err?.message ?? `Request failed (${res.status})`,
      );
    }
  } finally {
    release();
  }
}
